// ============================================================================
// src/webforms/WebformsEntry.jsx — the report/form surfaces' nested routes.
// ============================================================================
// B3.1 finish, sixth and last domain. Replaces resolveWebformsEntry.
//
// Every authenticated user (including Light) reaches these. Logged-out
// visitors hit the LoginScreen gate in App above the point where this entry is
// dispatched to; because the URL/view is preserved across login they return to
// the requested form afterward. THAT GATE STAYS IN APP -- it guards far more
// than this domain, and moving it here would narrow it. Two guards assert the
// ordering in two halves each (App reaches this entry only after the gate;
// this entry owns the seven views), and the anon-write boundary rests on them.
//
// See src/app/navigation/viewRoutes.js for why `location` is the ROUTED
// location, and src/webforms/webformsRoutes.js for why '/dailys/tasks' being
// nested inside '/dailys/*' is a precedence rule rather than a conflict.
// ============================================================================
import React from 'react';

import {AppRoute, AppRoutes} from '../app/navigation/index.js';
import AddFeedAuthenticatedBridge from '../app/data/AddFeedAuthenticatedBridge.js';
import FuelingAuthenticatedBridge from '../app/data/FuelingAuthenticatedBridge.js';
import PigDailysAuthenticatedBridge from '../app/data/PigDailysAuthenticatedBridge.js';
import TasksWebformAuthenticatedBridge from '../app/data/TasksWebformAuthenticatedBridge.js';
import WebformHubAuthenticatedBridge from '../app/data/WebformHubAuthenticatedBridge.js';
import {WEBFORMS_ROUTES} from './webformsRoutes.js';
import type {SessionSubmitter} from './webformsRoutes.js';

import PigDailysWebform from './PigDailysWebform.tsx';
import AddFeedWebform from './AddFeedWebform.tsx';
import WeighInsWebform from './WeighInsWebform.tsx';
import TasksWebform from './TasksWebform.tsx';
import WebformHub from './WebformHub.tsx';
import FuelingHub from './FuelingHub.tsx';
import FuelSupplyWebform from './FuelSupplyWebform.tsx';
import PageBoundary from '../app/PageBoundary.tsx';

export {WEBFORMS_ROUTES};

// Everything here arrives from App and leaves untouched, so each prop is typed
// by what it is handed to rather than by a shape restated in this file:
//
//   location   — forwarded verbatim to <AppRoutes>, so react-router owns it.
//                App produces it with routedLocationFor(), whose return type is
//                `AppLocation | string | null`, and only mounts this component
//                when that is non-null.
//   the other three — identity and role, not farm data. `sessionSubmitter` is
//                App's derivation from authState, and `isLight` / `role` decide
//                which tiles the hub hides.
//
// NO FARM DATA CROSSES THIS BOUNDARY ANY MORE.
//
// SIX PROPS LEFT AND NONE OF THEM MAY COME BACK.
//
// `layerBatches` and `layerHousings` went first: WebformHubAuthenticatedBridge
// reads both through webform.hub.layerDirectory and injects them beside the
// other bridge-owned values, so the hub no longer consumes App's former-eager
// layer_batches / layer_housings arrays. `batches` went with them because it
// was DEAD — the presenter destructured it and never read it, so forwarding it
// made a false claim about what /dailys depends on.
//
// `layerGroups` and `webformsConfig` followed, and they are what emptied
// view.webformhub's residual list. Both were app_store documents off App's
// global boot (ppp-layer-groups-v1 through LayerContext, ppp-webforms-v1
// through WebformsConfigContext); the same bridge reads them now through
// layer.eggDailys.groups and webform.pigDailys.config. `setWfGroups` went with
// them: it wrote a WebformsConfigContext mirror whose only reader was the hub's
// own fallback for the value the bridge had just produced, so the whole loop
// was inside this one surface.
//
// Do not reinstate any of the six here — a prop hand-off in this file is
// exactly what tests/static/former_eager_evidence_static.test.js scores as
// consumption, and the webformhub retirements in
// tests/fixtures/former-eager-dependencies.json would become false claims.
type WebformsEntryProps = {
  location?: React.ComponentProps<typeof AppRoutes>['location'];
  sessionSubmitter?: SessionSubmitter;
  isLight?: boolean;
  dailyHistoryPrograms?: readonly string[] | null;
  role?: string | null;
};

export function WebformsEntry({location, sessionSubmitter, isLight, role, dailyHistoryPrograms}: WebformsEntryProps) {
  const routes = (
    <AppRoutes location={location}>
      <AppRoute
        path="/webform-pigs"
        element={
          <PigDailysAuthenticatedBridge>
            <PigDailysWebform sessionSubmitter={sessionSubmitter} />
          </PigDailysAuthenticatedBridge>
        }
      />
      <AppRoute
        path="/addfeed"
        element={
          <AddFeedAuthenticatedBridge>
            <AddFeedWebform />
          </AddFeedAuthenticatedBridge>
        }
      />
      <AppRoute path="/weighins" element={<WeighInsWebform sessionSubmitter={sessionSubmitter} />} />
      <AppRoute
        path="/dailys/tasks"
        element={
          <TasksWebformAuthenticatedBridge>
            <TasksWebform />
          </TasksWebformAuthenticatedBridge>
        }
      />
      <AppRoute
        path="/dailys/*"
        element={
          <WebformHubAuthenticatedBridge>
            <WebformHub
              sessionSubmitter={sessionSubmitter}
              hideWeighIns={isLight}
              // Cattle Log excludes equipment_tech (server rejects the role);
              // hide the tile for them the same way Weigh-Ins hides for Light.
              hideCattleLog={role === 'equipment_tech'}
              dailyHistoryPrograms={dailyHistoryPrograms}
            />
          </WebformHubAuthenticatedBridge>
        }
      />
      <AppRoute
        path="/equipment/*"
        element={
          <FuelingAuthenticatedBridge>
            <FuelingHub />
          </FuelingAuthenticatedBridge>
        }
      />
      <AppRoute
        path="/fuel-supply"
        element={
          <FuelingAuthenticatedBridge>
            <FuelSupplyWebform />
          </FuelingAuthenticatedBridge>
        }
      />
    </AppRoutes>
  );
  // N12: a crash in the routed page stays inside it; App and the header
  // stay mounted (src/app/PageBoundary).
  return <PageBoundary>{routes}</PageBoundary>;
}

export default WebformsEntry;
